import AppContainer from "../Structure/AppContainer";
import React, { useContext, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import AuthContext from "../../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import useStateCallback from "../../util/customHooks/useStateCallback";
import ajaxPurchaseOrders from "../../util/remote/ajaxPurchaseOrders";
import PurchaseOrderDetailsModal from "./PurchaseOrderDetailsModal";

const ViewPurchaseOrders = () => {
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [meta, setMeta] = useState([]);
  const [page, setPage] = useState(1);

  // Date filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const today = new Date();
    const localDate = today.toLocaleDateString("en-CA"); // local date in YYYY-MM-DD
    setToDate(localDate);
  }, []);

  // Modals
  const [view_details, setView_details] = useStateCallback(false);

  // Fetch purchase orders
  const fetchPurchaseOrders = async () => {
    setLoading(true);
    const data = {
      page,
      from_date: fromDate || "",
      to_date: toDate || "",
    };
    setRequisitions([]);
    setMeta([]);
    const server_response = await ajaxPurchaseOrders.fetchPurchaseOrders(data);
    setLoading(false);
    if (server_response.status === "OK") {
      setRequisitions(server_response.details.list);
      setMeta(server_response.details.meta.list_of_pages);
    } else {
      toast.error(server_response.message);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
  }, [page]);

  // Pagination
  const setNextPageNumber = () => {
    if (page < meta.length) setPage(page + 1);
  };
  const setPreviousPageNumber = () => {
    if (page > 1) setPage(page - 1);
  };
  const setPageNumber = (e, item) => {
    e.preventDefault();
    setPage(item);
  };

  // Modal handlers
  const handleView_details = (data) => {
    setView_details(false, () =>
      setView_details(<PurchaseOrderDetailsModal data={data} />)
    );
  };

  // Filters
  const handleFilter = () => {
    setPage(1);
    fetchPurchaseOrders();
  };
  const handleResetFilters = () => {
    setFromDate("");
    const today = new Date().toISOString().split("T")[0];
    setToDate(today);
    setPage(1);
    fetchPurchaseOrders();
  };

  return (
    <AppContainer title="Purchase Orders">
      <div className="container-fluid">
        <Toaster />
        {view_details}

        {/* Create Button */}
        <div className="row mb-3">
          <div className="col-lg-3">
            <button
              className="btn btn-primary shadow-sm"
              onClick={() => navigate("/purchase_order/create")}
            >
              <i className="fa fa-plus me-2"></i> Create Purchase Order
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-header bg-light fw-bold">
            <i className="fa fa-filter me-2"></i> Filter Purchase Orders
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-lg-3">
                <label className="form-label">From Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-lg-3">
                <label className="form-label">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="col-lg-3 d-flex align-items-end">
                <button className="btn btn-success me-2" onClick={handleFilter}>
                  <i className="fa fa-search me-1"></i> Filter
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleResetFilters}
                >
                  <i className="fa fa-undo me-1"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-light fw-bold">
            <i className="fa fa-list me-2"></i> Organisation Purchase Orders
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Supplier</th>
                    <th>Narration</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requisitions.length > 0 ? (
                    requisitions.map((req, key) => (
                      <tr key={req.purID}>
                        <td>{key + 1}</td>
                        <td>
                          {req.buss_name} <br />
                          <span className="text-muted">{req.buss_contact}</span>
                        </td>
                        <td>{req.naration}</td>
                        <td>
                          <span className="badge bg-info">
                            {req.total_amount}
                          </span>
                        </td>
                        <td>
                          {req.status === "2" ? (
                            <span className="badge bg-success">
                              <i className="fa fa-check-circle me-1"></i>{" "}
                              Delivered
                            </span>
                          ) : (
                            <span className="badge bg-primary">Ordered</span>
                          )}
                        </td>
                        <td>{req.date_created}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-info me-2"
                            onClick={() => handleView_details(req)}
                          >
                            <i className="fa fa-eye"></i> Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-3">
                        {loading
                          ? "Loading purchase orders..."
                          : "No records found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="card-footer bg-light">
            <nav className="d-flex justify-content-center">
              <ul className="pagination mb-0">
                <li className="page-item">
                  <button className="page-link" onClick={setPreviousPageNumber}>
                    Prev
                  </button>
                </li>
                {Array.isArray(meta) &&
                  meta.map((item) =>
                    page === item ? (
                      <li className="page-item active" key={item}>
                        <span className="page-link">{item}</span>
                      </li>
                    ) : (
                      <li className="page-item" key={item}>
                        <a
                          className="page-link"
                          href="#"
                          onClick={(e) => setPageNumber(e, item)}
                        >
                          {item}
                        </a>
                      </li>
                    )
                  )}
                <li className="page-item">
                  <button className="page-link" onClick={setNextPageNumber}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </AppContainer>
  );
};

export default ViewPurchaseOrders;
